import { useState, useEffect, useCallback, startTransition } from "react";
import { FiStar, FiChevronLeft, FiChevronRight } from "@/lib/icons";

interface Testimonial {
  id?: string;
  name: string;
  location: string;
  content: string;
  rating: number;
  loanType: string;
}

// Testimonials are loaded from the API via admin panel
// This array is empty by default - add real testimonials via /admin/testimonials
const fallbackTestimonials: Testimonial[] = [];

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex gap-0.5">
      {[...Array(5)].map((_, i) => (
        <FiStar
          key={i}
          className={`size-4 ${
            i < rating
              ? "text-amber-400 fill-amber-400"
              : "text-muted-foreground"
          }`}
        />
      ))}
    </div>
  );
}

export function TestimonialsSection() {
  const [testimonials, setTestimonials] = useState<Testimonial[]>(fallbackTestimonials);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Fetch testimonials from API (non-blocking, low priority)
  useEffect(() => {
    // Small delay to not compete with critical page resources (images, etc.)
    const timeoutId = setTimeout(() => {
      const fetchTestimonials = async () => {
        try {
          const response = await fetch("/api/testimonials");
          if (response.ok) {
            const data = await response.json() as Testimonial[];
            if (data.length > 0) {
              // Use startTransition to mark this update as non-urgent
              startTransition(() => {
                setTestimonials(data);
                // Reset index to avoid out-of-bounds when data changes
                setCurrentIndex(0);
              });
            }
          }
        } catch {
          // Silently use fallback testimonials if API fails
        }
      };

      fetchTestimonials();
    }, 100); // 100ms delay lets critical content load first

    return () => clearTimeout(timeoutId);
  }, []);

  // Number of testimonials to show based on screen size (handled via CSS)
  const totalSlides = testimonials.length;

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % totalSlides);
  }, [totalSlides]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
  }, [totalSlides]);

  // Auto-rotate every 5 seconds, pause on hover
  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      nextSlide();
    }, 5000);

    return () => clearInterval(interval);
  }, [isPaused, nextSlide]);

  // Get visible testimonials (3 at a time for desktop, cycling through)
  const getVisibleTestimonials = () => {
    const visible = [];
    for (let i = 0; i < Math.min(3, totalSlides); i++) {
      visible.push(testimonials[(currentIndex + i) % totalSlides]);
    }
    return visible;
  };

  // Don't render if no testimonials
  if (testimonials.length === 0) {
    return null;
  }

  return (
    <section className="py-16 md:py-24 bg-cream overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <p className="section-label mb-4">Testimonials</p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl max-w-2xl mx-auto">
            What our clients say
          </h2>
          <p className="text-muted-foreground mt-4 max-w-xl mx-auto">
            Real stories from real clients we've helped on their home loan journey
          </p>
        </div>

        {/* Testimonials Carousel */}
        <div
          className="relative"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Navigation Arrows - positioned relative to fixed height container */}
          <div className="absolute -left-4 md:-left-6 top-0 bottom-0 flex items-center z-10">
            <button
              onClick={prevSlide}
              className="w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-accent hover:text-accent-foreground transition-colors hidden md:flex"
              aria-label="Previous testimonial"
            >
              <FiChevronLeft className="size-5" />
            </button>
          </div>
          <div className="absolute -right-4 md:-right-6 top-0 bottom-0 flex items-center z-10">
            <button
              onClick={nextSlide}
              className="w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-accent hover:text-accent-foreground transition-colors hidden md:flex"
              aria-label="Next testimonial"
            >
              <FiChevronRight className="size-5" />
            </button>
          </div>

          {/* Testimonials Grid - fixed height cards */}
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {getVisibleTestimonials().map((testimonial, index) => (
              <div
                key={`${currentIndex}-${index}`}
                className="bg-white rounded-2xl p-6 md:p-8 shadow-sm flex flex-col min-h-[280px] md:min-h-[300px]"
              >
                {/* Rating */}
                <StarRating rating={testimonial.rating} />

                {/* Content - fixed height with line clamp */}
                <blockquote className="mt-4 text-muted-foreground flex-1 line-clamp-5">
                  "{testimonial.content}"
                </blockquote>

                {/* Author - always at bottom */}
                <div className="mt-auto pt-4 border-t border-border">
                  <p className="font-medium text-foreground">{testimonial.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {testimonial.location} • {testimonial.loanType}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Dots Indicator - touch targets meet 44px minimum via padding */}
          <div className="flex justify-center gap-1 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className="p-3 -m-1 touch-manipulation"
                aria-label={`Go to testimonial ${index + 1}`}
              >
                <span
                  className={`block rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? "w-6 h-2 bg-primary"
                      : "w-2 h-2 bg-primary/50 hover:bg-primary/70"
                  }`}
                />
              </button>
            ))}
          </div>
        </div>

        {/* Trust indicator */}
        <div className="text-center mt-12">
          <p className="text-sm text-muted-foreground">
            Helping families across Australia
          </p>
        </div>
      </div>

    </section>
  );
}
